// src/features/admin/RederiveDistributedSplitCard.jsx
//
// HC-TREND-REDERIVE-UI: Self-contained card for the Maintenance tab that
// uploads a Workday CSV and re-derives ft/cw/ic counts in
// headcount_distributed_rollup for a given snapshot month.
//
// The endpoint is admin-gated server-side; this component is only rendered
// when currentRole === 'admin' (enforced in DataSection).

import React, { useState, useRef, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { DataTabHeading } from "../../components/DataTabHeading.jsx";
import { rederiveDistributedSplit } from "../../data/adminClient.js";

const BTN_BASE = {
  padding: "5px 12px",
  borderRadius: 6,
  fontSize: T.fsSm5,
  fontWeight: T.fwSemibold,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
};

const RESULT_BOX = {
  borderRadius: 6,
  padding: "10px 14px",
  fontSize: T.fsBase,
  color: T.dark,
  marginBottom: 12,
};

export function RederiveDistributedSplitCard() {
  const fileInputRef = useRef(null);
  const [file, setFile]             = useState(null);
  const [dateOverride, setDateOverride] = useState("");
  const [busy, setBusy]             = useState(false);
  const [result, setResult]         = useState(null);
  const [error, setError]           = useState(null);

  const handleFileChange = useCallback((e) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setError(null);
  }, []);

  const handleDateChange = useCallback((e) => {
    setDateOverride(e.target.value);
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const override = dateOverride.trim() || undefined;
      const res = await rederiveDistributedSplit(file, override);
      setResult(res);
    } catch (err) {
      setError(err?.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }, [file, dateOverride]);

  const handleReset = useCallback(() => {
    setFile(null);
    setDateOverride("");
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const canSubmit = !!file && !busy;

  return (
    <div data-testid="rederive-distributed-split-section">
      <DataTabHeading>Re-derive Distributed HC Split</DataTabHeading>
      <p style={{ fontSize: T.fsBase, color: T.mid, margin: "0 0 14px" }}>
        Upload the original Workday Active-Employees CSV for a historical month
        to back-fill the full-time / contractor / IC worker-type split in the
        distributed (remote) headcount rows. Only{" "}
        <code>headcount_distributed_rollup</code> is updated - no import
        session, no location headcount, and no other table is touched.
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          maxWidth: 480,
        }}
      >
        {/* File picker */}
        <label
          style={{
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            color: T.dark,
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          CSV file
          <input
            ref={fileInputRef}
            data-testid="rederive-file-input"
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            style={{ fontSize: T.fsBase, color: T.dark, cursor: "pointer" }}
          />
        </label>

        {/* Optional date override */}
        <label
          style={{
            fontSize: T.fsSm5,
            fontWeight: T.fwSemibold,
            color: T.dark,
            display: "flex",
            flexDirection: "column",
            gap: 5,
          }}
        >
          Snapshot date override{" "}
          <span style={{ fontWeight: T.fwRegular, color: T.mid }}>
            (optional - YYYY-MM-DD, only needed if the filename doesn't encode
            the date)
          </span>
          <input
            data-testid="rederive-date-input"
            type="text"
            placeholder="e.g. 2026-01-01"
            value={dateOverride}
            onChange={handleDateChange}
            maxLength={10}
            style={{
              fontSize: T.fsBase,
              color: T.dark,
              padding: "5px 8px",
              border: `1px solid ${T.border}`,
              borderRadius: 5,
              width: 160,
              background: T.card,
            }}
          />
        </label>

        {/* Action row */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
          <button
            type="button"
            data-testid="rederive-submit-btn"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              ...BTN_BASE,
              background: canSubmit ? T.blueDeep : T.light,
              color: T.white,
              border: "none",
              opacity: canSubmit ? 1 : 0.55,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {busy ? "Re-deriving…" : "Re-derive"}
          </button>

          {(file || result || error) && !busy && (
            <button
              type="button"
              data-testid="rederive-reset-btn"
              onClick={handleReset}
              style={{
                ...BTN_BASE,
                background: "transparent",
                color: T.mid,
                border: `1.5px solid ${T.border}`,
              }}
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Success result */}
      {result && (
        <div
          data-testid="rederive-result"
          role="status"
          style={{
            ...RESULT_BOX,
            marginTop: 14,
            background: T.alertSuccessBg,
            border: `1px solid ${T.alertSuccessBorder}`,
          }}
        >
          <div style={{ fontWeight: T.fwSemibold, marginBottom: 6 }}>
            Re-derive complete - snapshot {result.snapshotDate}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: T.fsSm5, color: T.mid }}>
            <span>
              <strong style={{ color: T.dark }}>{result.updated}</strong> geo row
              {result.updated !== 1 ? "s" : ""} updated
            </span>
            {result.notFound > 0 && (
              <span>
                <strong style={{ color: T.dark }}>{result.notFound}</strong> geo label
                {result.notFound !== 1 ? "s" : ""} not found in DB (new labels from re-upload are skipped)
              </span>
            )}
            {result.unmappedRowCount > 0 && (
              <span>
                {result.unmappedRowCount} CSV row
                {result.unmappedRowCount !== 1 ? "s" : ""} had no OfficeSpace ID (counted as distributed)
              </span>
            )}
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: T.fsSm,
              color: T.mid,
              fontStyle: "italic",
            }}
          >
            {result.message}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          data-testid="rederive-error"
          role="alert"
          style={{
            ...RESULT_BOX,
            marginTop: 14,
            background: T.alertErrBg,
            border: `1px solid ${T.red}`,
          }}
        >
          <strong style={{ color: T.red }}>Error: </strong>
          {error}
        </div>
      )}
    </div>
  );
}
